import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SocketProvider } from "@/context/SocketContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { TopAppBar } from "@/components/navigation/TopAppBar";
import { BottomTabNav } from "@/components/navigation/BottomTabNav";
import { Skeleton } from "@/components/ui/skeleton";

import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import AthleteHome from "@/pages/athlete/AthleteHome";
import AthleteVerifyPage from "@/pages/athlete/AthleteVerifyPage";
import BookingWizard from "@/pages/athlete/BookingWizard";
import BookingDetail from "@/pages/BookingDetail";
import BookingsList from "@/pages/BookingsList";
import BallboyHome from "@/pages/ballboy/BallboyHome";
import JobsBoard from "@/pages/ballboy/JobsBoard";
import ProfilePage from "@/pages/ProfilePage";
import AdminQueue from "@/pages/admin/AdminQueue";
import ChatThread from "@/pages/ChatThread";
import MessagesList from "@/pages/MessagesList";
import NotificationsPage from "@/pages/NotificationsPage";
import TermsOfService from "@/pages/legal/TermsOfService";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";

function Loader() {
  return (
    <div className="min-h-screen p-4 space-y-3 max-w-[560px] mx-auto">
      <Skeleton className="h-14" />
      <Skeleton className="h-40" />
      <Skeleton className="h-24" />
    </div>
  );
}

function RequireAuth({ roles, children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (roles && !roles.includes(user.role))
    return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;
  return children;
}

function AppShell({ children, title }) {
  return (
    <div className="min-h-[100dvh] pb-[96px]">
      <TopAppBar title={title} />
      <main className="max-w-[560px] mx-auto px-4 py-4">{children}</main>
      <BottomTabNav />
    </div>
  );
}

function RoleHome() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "athlete") return <AthleteHome />;
  if (user.role === "ballboy") return <BallboyHome />;
  return <Navigate to="/admin" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SocketProvider>
          <NotificationProvider>
            <Toaster position="top-center" richColors closeButton />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/login" element={<AuthPage mode="login" />} />
              <Route path="/signup" element={<AuthPage mode="signup" />} />

              <Route
                path="/athlete/verify"
                element={
                  <RequireAuth roles={["athlete"]}>
                    <AppShell title="Trust & Verification"><AthleteVerifyPage /></AppShell>
                  </RequireAuth>
                }
              />

              <Route
                path="/app"
                element={
                  <RequireAuth>
                    <AppShell title="BallBoy"><RoleHome /></AppShell>
                  </RequireAuth>
                }
              />
              <Route
                path="/request"
                element={
                  <RequireAuth roles={["athlete"]}>
                    <AppShell title="New booking"><BookingWizard /></AppShell>
                  </RequireAuth>
                }
              />
              <Route
                path="/bookings"
                element={
                  <RequireAuth roles={["athlete", "ballboy"]}>
                    <AppShell title={null}><BookingsList /></AppShell>
                  </RequireAuth>
                }
              />
              <Route
                path="/bookings/:id"
                element={
                  <RequireAuth>
                    <AppShell title="Booking"><BookingDetail /></AppShell>
                  </RequireAuth>
                }
              />
              <Route
                path="/jobs"
                element={
                  <RequireAuth roles={["ballboy"]}>
                    <AppShell title="Jobs"><JobsBoard /></AppShell>
                  </RequireAuth>
                }
              />
              <Route
                path="/messages"
                element={
                  <RequireAuth roles={["athlete", "ballboy"]}>
                    <AppShell title="Messages"><MessagesList /></AppShell>
                  </RequireAuth>
                }
              />
              <Route
                path="/messages/:bookingId"
                element={
                  <RequireAuth>
                    <AppShell title="Chat"><ChatThread /></AppShell>
                  </RequireAuth>
                }
              />
              <Route
                path="/profile"
                element={
                  <RequireAuth>
                    <AppShell title="Profile"><ProfilePage /></AppShell>
                  </RequireAuth>
                }
              />
              <Route
                path="/notifications"
                element={
                  <RequireAuth>
                    <AppShell title="Notifications"><NotificationsPage /></AppShell>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin"
                element={
                  <RequireAuth roles={["admin"]}>
                    <AppShell title="Admin"><AdminQueue /></AppShell>
                  </RequireAuth>
                }
              />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </NotificationProvider>
        </SocketProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
